import { captureException } from '@sentry/react';
import api from '../libs/axiosInstance'; // This should point to the API project (port 3000)
import firebase from 'firebase/app';
import 'firebase/auth';

interface ValidationResult {
  allowed: boolean;
  message: string;
  used: number;
  limit: number;
  canUseCredits?: boolean;
  creditCost?: number;
  remainingCredits?: number;
  suggestedAction?: string;
  purchaseUrl?: string;
}

interface UsageSummary {
  limits: {
    interviewSessions: number;
    jobAnalysisSessions: number;
    cvGenerations: number;
    cvStorageLimit: number;
  };
  currentUsage: {
    interviewSessionCount: number;
    jobAnalysisCount: number;
    cvGenerationCount: number;
    cvStorageCount: number;
  };
  features: {
    companyResearch: boolean;
    skillsGapAnalysis: string;
    atsOptimization: boolean;
    exportFormats: string[];
  };
  planName: string;
}

/**
 * Service to interact with the API project for usage validation and subscription management
 * This ensures all payment/subscription logic stays in the API project
 */
export class APIUsageService {

  /**
   * Get the current user's subscription details from API project
   */
  async getUserSubscription(): Promise<any> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.get(`/usage/${user.uid}/subscription`);

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to get subscription');
    } catch (error) {
      console.error('Error getting user subscription:', error);
      throw error;
    }
  }

  /**
   * Get complete usage summary from API project
   */
  async getUsageSummary(): Promise<UsageSummary> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.get(`/usage/${user.uid}/summary`);

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to get usage summary');
    } catch (error) {
      console.error('Error getting usage summary:', error);
      throw error;
    }
  }

  /**
   * Check if user can create job analysis
   */
  async canCreateJobAnalysis(): Promise<ValidationResult> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.get(`/usage/${user.uid}/can-create-job-analysis`);

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to check job analysis access');
    } catch (error) {
      console.error('Error checking job analysis access:', error);
      throw error;
    }
  }

  /**
   * Check if user can generate CV
   */
  async canGenerateCV(): Promise<ValidationResult> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.get(`/usage/${user.uid}/can-generate-cv`);

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to check CV generation access');
    } catch (error) {
      console.error('Error checking CV generation access:', error);
      throw error;
    }
  }

  /**
   * Check if user can store more CVs
   */
  async canStoreCV(): Promise<ValidationResult> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.get(`/usage/${user.uid}/can-store-cv`);

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to check CV storage access');
    } catch (error) {
      console.error('Error checking CV storage access:', error);
      throw error;
    }
  }

  /**
   * Check if user can access company research
   */
  async canAccessCompanyResearch(): Promise<ValidationResult> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.get(`/usage/${user.uid}/can-access-company-research`);

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to check company research access');
    } catch (error) {
      console.error('Error checking company research access:', error);
      throw error;
    }
  }

  /**
   * Check if user can access skills gap analysis
   */
  async canAccessSkillsGapAnalysis(): Promise<ValidationResult> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.get(`/usage/${user.uid}/can-access-skills-gap-analysis`);

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to check skills gap analysis access');
    } catch (error) {
      console.error('Error checking skills gap analysis access:', error);
      throw error;
    }
  }

  /**
   * Check if user can access ATS optimization
   */
  async canAccessATSOptimization(): Promise<ValidationResult> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.get(`/usage/${user.uid}/can-access-ats-optimization`);

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to check ATS optimization access');
    } catch (error) {
      console.error('Error checking ATS optimization access:', error);
      throw error;
    }
  }

  /**
   * Check feature access
   */
  async hasFeatureAccess(feature: string): Promise<boolean> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.get(`/usage/${user.uid}/feature/${feature}`);

      if (response.data.success) {
        return response.data.data.hasAccess;
      }

      throw new Error(response.data.message || 'Failed to check feature access');
    } catch (error) {
      console.error('Error checking feature access:', error);
      return false;
    }
  }

  /**
   * Check export format access
   */
  async canExportFormat(format: string): Promise<boolean> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.get(`/usage/${user.uid}/export/${format}`);

      if (response.data.success) {
        return response.data.data.canExport;
      }

      throw new Error(response.data.message || 'Failed to check export format access');
    } catch (error) {
      console.error('Error checking export format access:', error);
      return false;
    }
  }

  /**
   * Increment job analysis usage (called after successful job analysis)
   */
  async incrementJobAnalysisUsage(): Promise<void> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.post(`/usage/${user.uid}/increment-job-analysis-with-allowances`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to increment job analysis usage');
      }
    } catch (error) {
      console.error('Error incrementing job analysis usage:', error);
      // Don't throw as this is tracking only
    }
  }

  /**
   * Increment CV generation usage (called after successful CV generation)
   */
  async incrementCVGenerationUsage(): Promise<void> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.post(`/usage/${user.uid}/increment-cv-generation-with-allowances`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to increment CV generation usage');
      }
    } catch (error) {
      console.error('Error incrementing CV generation usage:', error);
      // Don't throw as this is tracking only
    }
  }

  /**
   * Check if user can create interview session
   */
  async canCreateInterviewSession(): Promise<ValidationResult> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.get(`/usage/${user.uid}/can-create-interview-session`);

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to check interview session access');
    } catch (error) {
      console.error('Error checking interview session access:', error);
      throw error;
    }
  }

  /**
   * Increment interview session usage (called after successful session creation)
   */
  async incrementInterviewSessionUsage(): Promise<void> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.post(`/usage/${user.uid}/increment-interview-session-with-allowances`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to increment interview session usage');
      }
    } catch (error) {
      console.error('Error incrementing interview session usage:', error);
      // Don't throw as this is tracking only
    }
  }

  /**
   * Check if user can create practice attempt
   */
  async canCreatePracticeAttempt(sessionId?: string): Promise<ValidationResult> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const url = sessionId
        ? `/usage/${user.uid}/can-create-practice-attempt?sessionId=${sessionId}`
        : `/usage/${user.uid}/can-create-practice-attempt`;

      const response = await api.get(url);

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to check practice attempt access');
    } catch (error) {
      captureException(error);
      throw error;
    }
  }

  /**
   * Increment practice attempt usage (called after successful practice attempt)
   */
  async incrementPracticeAttemptUsage(): Promise<void> {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await api.post(`/usage/${user.uid}/increment-practice-attempt`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to increment practice attempt usage');
      }
    } catch (error) {
      captureException(error);
      // Don't throw as this is tracking only
    }
  }
}

// Export singleton instance
export const apiUsageService = new APIUsageService();
